/**
 * AI Website Booking Agent transcripts page (iter-12f)
 *
 * Backoffice-only viewer for chat transcripts recorded against the
 * ai_booking_agent add-on. Two panes:
 *   - Sessions list: paginated summary of agent_sessions for the vendor,
 *     newest first, with message counts and last-message time.
 *   - Detail pane: full messages array for the selected session, rendered
 *     as a chat log with role tags. Metadata (locale, referrer host) is
 *     shown above the transcript.
 *
 * The page reuses the settings-page activation pattern: if the API
 * returns 409 (add-on inactive), we render the same activation prompt
 * seen on the settings page. Otherwise the page fetches state on mount.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'

const PAGE_SIZE = 25

interface SessionSummary {
  session_id: string
  customer_fingerprint: string | null
  message_count: number
  last_message_at: string | null
  created_at: string
  expires_at: string
  locale: string | null
  referrer_host: string | null
  handoffs?: HandoffCase[]
}

/** A request the agent passed to the supplier (TIX-017). */
interface HandoffCase {
  reference: string
  reason: string
  summary: string
  guest_name: string | null
  guest_email: string | null
  booking_reference: string | null
  status: string
  created_at: string
}

interface AgentMessage {
  role: 'system' | 'user' | 'assistant' | 'tool'
  content: string | null
  tool_calls?: Array<{ id: string; name: string; arguments: string }>
  tool_call_id?: string
  name?: string
  created_at?: string
}

interface SessionDetail extends SessionSummary {
  metadata: Record<string, unknown>
  messages: AgentMessage[]
}

export default function AgentTranscriptsPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const [page, setPage] = useState(0)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [requestsOnly, setRequestsOnly] = useState(false)

  const listQuery = useQuery({
    queryKey: ['agent-sessions', vendorId, page, requestsOnly],
    queryFn: () =>
      api.addOns.listAgentSessions(vendorId!, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        ...(requestsOnly ? { handoffs: 1 } : {}),
      }),
    enabled: !!vendorId,
    retry: (failureCount, err: any) => {
      if (err?.response?.status === 409) return false
      return failureCount < 2
    },
  })

  const detailQuery = useQuery({
    queryKey: ['agent-session', vendorId, selectedId],
    queryFn: () => api.addOns.getAgentSession(vendorId!, selectedId!),
    enabled: !!vendorId && !!selectedId,
  })

  const notActive = (listQuery.error as any)?.response?.status === 409

  if (!vendorId) {
    return (
      <div style={pageStyle}>
        <p>Sign in as a vendor to view agent transcripts.</p>
      </div>
    )
  }

  if (notActive) {
    return (
      <div style={pageStyle}>
        <h1 style={{ marginBottom: '0.5rem' }}>Agent transcripts</h1>
        <p style={{ color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
          The AI Website Booking Agent add-on is not active on this account.
          Activate it from the Add-ons page to start collecting transcripts.
        </p>
        <a
          href="/vendor/add-ons"
          style={{
            display: 'inline-block',
            marginTop: '1rem',
            padding: '0.6rem 1.2rem',
            fontSize: '0.9rem',
            fontWeight: 600,
            background: 'var(--color-tixxly-navy)',
            color: 'var(--color-pure-white)',
            border: 'none',
            borderRadius: 6,
            textDecoration: 'none',
          }}
        >
          Go to Add-ons
        </a>
      </div>
    )
  }

  const sessions =
    ((listQuery.data?.data as any)?.data as SessionSummary[] | undefined) || []
  const pagination = (listQuery.data?.data as any)?.pagination as
    | { limit: number; offset: number; total: number }
    | undefined
  const total = pagination?.total ?? 0
  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1)

  const detail =
    ((detailQuery.data?.data as any)?.data as SessionDetail | undefined) ||
    null

  return (
    <div style={pageStyle}>
      <h1 style={{ marginBottom: '0.5rem' }}>Agent transcripts</h1>
      <p
        style={{
          color: 'var(--color-gray-600)',
          marginBottom: '2rem',
          lineHeight: 1.6,
        }}
      >
        Full chat logs recorded by the AI Website Booking Agent widget on
        your own website. Sessions expire 24 hours after the last message.
        Conversations
        where the agent passed a request to you (a cancellation, change,
        refund, complaint or a visitor asking for a person) are kept, with the
        visitor's email so you can reply.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 380px) 1fr',
          gap: '1.5rem',
          alignItems: 'start',
        }}
      >
        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Sessions</h2>
          <label style={{ ...helperTextStyle, display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={requestsOnly}
              onChange={(event) => {
                setRequestsOnly(event.target.checked)
                setPage(0)
              }}
            />
            Only conversations with a request for you
          </label>
          {listQuery.isLoading ? (
            <p style={helperTextStyle}>Loading sessions...</p>
          ) : sessions.length === 0 ? (
            <p style={helperTextStyle}>No sessions recorded yet.</p>
          ) : (
            <ul style={sessionListStyle}>
              {sessions.map((session) => (
                <li
                  key={session.session_id}
                  onClick={() => setSelectedId(session.session_id)}
                  style={{
                    ...sessionListItemStyle,
                    background:
                      selectedId === session.session_id
                        ? 'var(--color-gray-50, #f7f7f7)'
                        : 'transparent',
                    borderLeft:
                      selectedId === session.session_id
                        ? '3px solid var(--color-tixxly-navy)'
                        : '3px solid transparent',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {new Date(session.created_at).toLocaleString()}
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-gray-500)',
                      marginTop: '0.25rem',
                    }}
                  >
                    {session.message_count} messages
                    {session.referrer_host
                      ? ` \u00b7 ${session.referrer_host}`
                      : ''}
                    {session.locale ? ` \u00b7 ${session.locale}` : ''}
                  </div>
                  {session.handoffs && session.handoffs.length > 0 && (
                    <div
                      style={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: 'var(--color-tixxly-navy)',
                        marginTop: '0.25rem',
                      }}
                    >
                      {session.handoffs.length === 1
                        ? '1 request for you'
                        : `${session.handoffs.length} requests for you`}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {sessions.length > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '1rem',
                fontSize: '0.85rem',
              }}
            >
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(p - 1, 0))}
                style={secondaryButtonStyle}
              >
                Previous
              </button>
              <span style={{ color: 'var(--color-gray-500)' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={secondaryButtonStyle}
              >
                Next
              </button>
            </div>
          )}
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Transcript</h2>
          {!selectedId ? (
            <p style={helperTextStyle}>
              Select a session on the left to read the transcript.
            </p>
          ) : detailQuery.isLoading ? (
            <p style={helperTextStyle}>Loading transcript...</p>
          ) : !detail ? (
            <p style={helperTextStyle}>Transcript not available.</p>
          ) : (
            <>
              <div
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--color-gray-500)',
                  marginBottom: '1rem',
                  lineHeight: 1.5,
                }}
              >
                <div>
                  Started {new Date(detail.created_at).toLocaleString()}
                </div>
                <div>
                  Last message{' '}
                  {detail.last_message_at
                    ? new Date(detail.last_message_at).toLocaleString()
                    : 'never'}
                </div>
                <div>
                  Expires {new Date(detail.expires_at).toLocaleString()}
                </div>
                {detail.customer_fingerprint && (
                  <div>Fingerprint: {detail.customer_fingerprint}</div>
                )}
              </div>

              {Array.isArray((detail.metadata as any)?.handoffs) &&
                ((detail.metadata as any).handoffs as HandoffCase[]).map((h) => (
                  <div
                    key={h.reference}
                    style={{
                      border: '1px solid var(--color-gray-200, #e5e5e5)',
                      borderRadius: 6,
                      padding: '0.75rem 1rem',
                      marginBottom: '1rem',
                      fontSize: '0.9rem',
                      lineHeight: 1.5,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      Request {h.reference}: {h.reason}
                    </div>
                    <div>{h.summary}</div>
                    <div style={{ color: 'var(--color-gray-600)' }}>
                      {[h.guest_name, h.guest_email, h.booking_reference && `Booking ${h.booking_reference}`]
                        .filter(Boolean)
                        .join(' \u00b7 ')}
                      {' \u00b7 '}
                      {new Date(h.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}

              {detail.messages.length === 0 ? (
                <p style={helperTextStyle}>No messages in this session.</p>
              ) : (
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {detail.messages.map((msg, index) => (
                    <div
                      key={index}
                      style={{
                        border: '1px solid var(--color-gray-200)',
                        borderRadius: 6,
                        padding: '0.75rem 1rem',
                        background:
                          msg.role === 'user'
                            ? 'var(--color-pure-white, #fff)'
                            : msg.role === 'assistant'
                              ? 'var(--color-gray-50, #f7f7f7)'
                              : 'var(--color-gray-100, #f0f0f0)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'var(--color-gray-500)',
                          marginBottom: '0.35rem',
                        }}
                      >
                        {msg.role}
                        {msg.name ? ` \u00b7 ${msg.name}` : ''}
                      </div>
                      {msg.content && (
                        <div
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '0.9rem',
                            lineHeight: 1.5,
                          }}
                        >
                          {msg.content}
                        </div>
                      )}
                      {msg.tool_calls && msg.tool_calls.length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                          {msg.tool_calls.map((call) => (
                            <div
                              key={call.id}
                              style={{
                                fontFamily:
                                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                fontSize: '0.75rem',
                                color: 'var(--color-gray-600)',
                                background: 'var(--color-pure-white, #fff)',
                                border:
                                  '1px solid var(--color-gray-200)',
                                borderRadius: 4,
                                padding: '0.5rem',
                                marginBottom: '0.25rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                              }}
                            >
                              {call.name}({call.arguments})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}

const pageStyle = {
  padding: '2rem',
  maxWidth: 1200,
  margin: '0 auto',
} as const

const sectionStyle = {
  border: '1px solid var(--color-gray-200)',
  borderRadius: 8,
  padding: '1.5rem',
  background: 'var(--color-pure-white, #fff)',
} as const

const sectionHeadingStyle = {
  fontSize: '1.05rem',
  fontWeight: 600,
  margin: '0 0 1rem',
} as const

const helperTextStyle = {
  margin: '0 0 0.75rem',
  color: 'var(--color-gray-500)',
  fontSize: '0.9rem',
  lineHeight: 1.5,
} as const

const sessionListStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  maxHeight: 520,
  overflowY: 'auto',
} as const

const sessionListItemStyle = {
  padding: '0.75rem 0.75rem',
  cursor: 'pointer',
  borderBottom: '1px solid var(--color-gray-100, #eee)',
  transition: 'background 0.1s ease',
} as const

const secondaryButtonStyle = {
  padding: '0.4rem 0.9rem',
  fontSize: '0.85rem',
  fontWeight: 600,
  background: 'var(--color-pure-white)',
  color: 'var(--color-tixxly-navy)',
  border: '1px solid var(--color-tixxly-navy)',
  borderRadius: 6,
  cursor: 'pointer',
} as const
